/**
 * Affiliate Hook
 *
 * React Query hooks for affiliate program
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { affiliateService } from '@/services/affiliate.service';
import type { PayoutRequest } from '@/types/affiliate';

/**
 * Hook for fetching affiliate stats
 */
export const useAffiliateStats = () => {
  return useQuery({
    queryKey: ['affiliate', 'stats'],
    queryFn: affiliateService.getStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for joining affiliate program
 */
export const useJoinAffiliate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: affiliateService.join,
    onSuccess: () => {
      // Invalidate all affiliate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['affiliate'] });
    },
  });
};

/**
 * Hook for fetching conversions
 */
export const useAffiliateConversions = (params?: {
  status?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['affiliate', 'conversions', params],
    queryFn: () => affiliateService.getConversions(params),
    enabled: false, // Only fetch when explicitly enabled
  });
};

/**
 * Hook for fetching payouts
 */
export const useAffiliatePayouts = (params?: {
  status?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['affiliate', 'payouts', params],
    queryFn: () => affiliateService.getPayouts(params),
    enabled: false, // Only fetch when explicitly enabled
  });
};

/**
 * Hook for requesting payout
 */
export const useRequestPayout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PayoutRequest) => affiliateService.requestPayout(data),
    onSuccess: () => {
      // Invalidate all affiliate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['affiliate'] });
    },
  });
};

/**
 * Combined convenience hook for affiliate data and actions
 */
export const useAffiliate = () => {
  const statsQuery = useAffiliateStats();
  const joinMutation = useJoinAffiliate();
  const payoutMutation = useRequestPayout();

  return {
    // Stats
    stats: statsQuery.data,
    isLoading: statsQuery.isLoading,
    error: statsQuery.error,
    refetch: statsQuery.refetch,

    // Join
    join: joinMutation.mutate,
    isJoining: joinMutation.isPending,
    joinError: joinMutation.error,

    // Payout
    requestPayout: payoutMutation.mutate,
    isRequestingPayout: payoutMutation.isPending,
    payoutError: payoutMutation.error,

    // Computed values
    isAffiliate: statsQuery.data?.isAffiliate ?? false,
    referralLink: statsQuery.data?.referralLink,
    referralCode: statsQuery.data?.referralCode,
    availableBalance: statsQuery.data?.stats?.availableBalance ?? 0,
  };
};
